import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Star } from 'lucide-react';
import { Stat } from '@/shared/ui';
import type { OverviewFlags, OverviewReputation } from '../api/overviewApi';
import styles from './ReputationSection.module.css';

interface ReputationSectionProps {
  flags: OverviewFlags;
  data: OverviewReputation;
}

export function ReputationSection({ flags, data }: ReputationSectionProps) {
  const navigate = useNavigate();
  const sourceLabel = data.source === 'gbp' ? 'via GBP' : 'scanned from Google';

  return (
    <div className={styles.grid}>
      {/* Rating summary */}
      <div className={styles.card}>
        <div className={styles.summary}>
          <Stat
            label="Google Reviews"
            size="xl"
            value={
              <span className={styles.ratingRow}>
                {data.rating?.toFixed(1) ?? '—'}
                <span className={styles.star}>
                  <Star size={20} fill="currentColor" aria-hidden />
                </span>
              </span>
            }
          />
          <div className={styles.sub}>
            {data.reviewCount ?? 0} reviews · {sourceLabel}
          </div>
        </div>
        <div className={styles.distribution}>
          {data.distribution.map(({ star, pct }) => (
            <div key={star} className={styles.distRow}>
              <div className={styles.distLabel}>
                {star}
                <Star size={11} fill="currentColor" aria-hidden />
              </div>
              <div className={styles.distBar}>
                <div
                  className={clsx(
                    styles.distFill,
                    pct > 50 ? styles.distFillAccent : styles.distFillWarning,
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className={styles.distPct}>{pct}%</div>
            </div>
          ))}
        </div>
        {data.neighborhoodAvgReviews !== null && data.neighborhoodAvgRating !== null && (
          <div className={styles.note}>
            {(data.reviewCount ?? 0) > data.neighborhoodAvgReviews ? (
              <>
                <span className={styles.noteAccent}>Ahead</span> of most{' '}
                {data.neighborhoodCity ?? 'local'} practices · avg {data.neighborhoodAvgReviews}{' '}
                reviews, {data.neighborhoodAvgRating}
                <Star size={11} fill="currentColor" aria-hidden />
              </>
            ) : (
              <>
                Local average: {data.neighborhoodAvgReviews} reviews, {data.neighborhoodAvgRating}
                <Star size={11} fill="currentColor" aria-hidden />
              </>
            )}
          </div>
        )}
        {!flags.hasSEO && (
          <button
            type="button"
            className={styles.link}
            onClick={() => navigate('/settings/organic')}
          >
            Connect GBP to enable auto-responses →
          </button>
        )}
      </div>

      {/* Recent reviews */}
      <div className={styles.card}>
        <div className={styles.recentHeader}>
          <div className={styles.recentTitle}>Recent Reviews</div>
          {data.newLast30Days != null && data.newLast30Days > 0 ? (
            <div className={styles.recentBadge}>{data.newLast30Days} new in last 30 days</div>
          ) : data.totalDisplayed > 0 ? (
            <div className={styles.recentBadgeWarn}>0 new in last 30 days</div>
          ) : null}
        </div>
        <div className={styles.reviews}>
          {data.recentReviews.length === 0 ? (
            <div className={styles.empty}>No recent reviews yet.</div>
          ) : (
            data.recentReviews.map((r, i) => (
              <div
                key={i}
                className={clsx(
                  styles.review,
                  i < data.recentReviews.length - 1 && styles.reviewDivider,
                )}
              >
                <div className={styles.reviewMeta}>
                  <div className={styles.stars}>
                    <span className={styles.starsFilled}>
                      {Array.from({ length: r.stars }, (_, j) => (
                        <Star key={`f-${j}`} size={12} fill="currentColor" aria-hidden />
                      ))}
                    </span>
                    <span className={styles.starsDim}>
                      {Array.from({ length: 5 - r.stars }, (_, j) => (
                        <Star key={`e-${j}`} size={12} aria-hidden />
                      ))}
                    </span>
                  </div>
                  <div className={styles.reviewPlatform}>
                    {r.reviewerName && <span>{r.reviewerName}</span>}
                    <span>{r.platform}</span>
                    {r.date && <span>{formatDate(r.date)}</span>}
                  </div>
                </div>
                <div className={styles.reviewText}>"{r.text}"</div>
                {r.hasReply && r.replyText ? (
                  <div className={styles.reviewReply}>
                    <span className={styles.replyLabel}>Reply:</span> {r.replyText}
                  </div>
                ) : (
                  <div className={styles.reviewNoReply}>No response yet</div>
                )}
              </div>
            ))
          )}
        </div>
        <div className={styles.footer}>
          {data.totalDisplayed > 0
            ? `${data.repliedCount} of ${data.totalDisplayed} recent reviews have a response`
            : 'No reviews yet'}
        </div>
        <button type="button" className={styles.link} onClick={() => navigate('/seo/reviews')}>
          View all reviews →
        </button>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}
